<template>
  <section class="k12-panel">
    <div class="k12-panel-header">
      <div>
        <div class="k12-panel-title">
          <span class="k12-icon"><i class="fas fa-receipt"></i></span>
          <span>{{ $t('preInvoiceV2.feeItems') }}</span>
        </div>
        <div class="section-subtitle">{{ $t('preInvoiceV2.coveredFeeHidden') }}</div>
      </div>
    </div>

    <div class="k12-panel-body">
      <div v-if="hasFeeSectionError()" class="fee-section-error">{{ feeSectionErrorMessage() }}</div>

      <div v-if="!model.items.length" class="fee-empty-state">
        <span class="fee-empty-icon"><i class="fas fa-receipt"></i></span>
        <div>
          <strong>{{ $t('preInvoiceV2.noFeeItemsYet') }}</strong>
          <span>{{ canLoadFees ? $t('preInvoiceV2.addFeeItemHint') : $t('preInvoiceV2.completeAcademicBeforeFee') }}</span>
        </div>
        <a-button type="primary" icon="plus" :disabled="!canLoadFees" @click="$emit('add-item')">
          {{ $t('preInvoiceV2.addItem') }}
        </a-button>
      </div>

      <div v-else class="ts-overflow-x-auto">
        <table class="ts-w-full ts-table-fixed pre-invoice-table">
          <thead>
            <tr>
              <th style="width: 20px;">#</th>
              <th style="width: 170px;">{{ $t('preInvoiceV2.feeType') }}</th>
              <th style="width: 240px;">{{ $t('preInvoiceV2.feeName') }}</th>
              <th style="width: 76px;">{{ $t('preInvoiceV2.quantity') }}</th>
              <th style="width: 130px;">{{ $t('preInvoiceV2.startDate') }}</th>
              <th style="width: 130px;">{{ $t('preInvoiceV2.endDate') }}</th>
              <th style="width: 112px;">{{ $t('preInvoiceV2.feeAmount') }}</th>
              <th style="width: 96px;">{{ $t('preInvoiceV2.discountType') }}</th>
              <th style="width: 112px;">{{ $t('preInvoiceV2.discountAmount') }}</th>
              <th style="width: 130px;">{{ $t('preInvoiceV2.amountIncludeVat') }}</th>
              <th style="width: 64px;" class="ts-text-center">{{ $t('preInvoiceV2.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in model.items" :key="index">
              <td class="fee-row-number">{{ index + 1 }}</td>
              <td>
                <div class="fee-cell-control">
                  <a-select
                    v-model="item.fee_type_id"
                    class="ts-w-full"
                    :class="{ 'is-invalid': hasItemError(index, 'fee_type_id') }"
                    :placeholder="$t('preInvoiceV2.feeType')"
                    @change="$emit('fee-type-change', index)"
                  >
                    <a-select-option v-for="type in feeTypes" :key="type.fee_type_id" :value="type.fee_type_id">{{ type.fee_type_name }}</a-select-option>
                  </a-select>
                  <div v-if="hasItemError(index, 'fee_type_id')" class="fee-field-error">{{ itemError(index, 'fee_type_id') }}</div>
                </div>
              </td>
              <td>
                <div class="fee-cell-control">
                  <a-select
                    v-model="item.fee_setup_id"
                    class="ts-w-full"
                    :class="{ 'is-invalid': hasItemError(index, 'fee_setup_id') }"
                    :disabled="!item.fee_type_id"
                    :placeholder="$t('preInvoiceV2.selectFeeItem')"
                    @change="$emit('fee-item-change', index)"
                  >
                    <a-select-option
                      v-for="fee in item.feeItems"
                      :key="fee.fee_setup_id"
                      :value="fee.fee_setup_id"
                      :disabled="isFeeSetupSelected(fee.fee_setup_id, index)"
                    >
                      {{ fee.fee_description }}
                    </a-select-option>
                  </a-select>
                  <div v-if="hasItemError(index, 'fee_setup_id')" class="fee-field-error">{{ itemError(index, 'fee_setup_id') }}</div>
                </div>
              </td>
              <td>
                <div class="fee-cell-control">
                  <a-input-number v-model="item.quantity" class="ts-w-full" :class="{ 'is-invalid': hasItemError(index, 'quantity') }" :min="1" @change="$emit('date-range-change', index)" />
                  <div v-if="hasItemError(index, 'quantity')" class="fee-field-error">{{ itemError(index, 'quantity') }}</div>
                </div>
              </td>
              <td>
                <div class="fee-cell-control">
                  <a-date-picker v-model="item.fee_start_date" class="ts-w-full" :class="{ 'is-invalid': hasItemError(index, 'fee_start_date') }" :disabled-date="date => disabledStartDate(date, item)" format="DD-MM-YYYY" valueFormat="DD-MM-YYYY" @change="$emit('date-range-change', index)" />
                  <div v-if="hasItemError(index, 'fee_start_date')" class="fee-field-error">{{ itemError(index, 'fee_start_date') }}</div>
                </div>
              </td>
              <td>
                <div class="fee-cell-control">
                  <a-date-picker v-model="item.fee_end_date" class="ts-w-full" :class="{ 'is-invalid': hasItemError(index, 'fee_end_date') }" :disabled="hasPaymentTerm(item)" format="DD-MM-YYYY" valueFormat="DD-MM-YYYY" />
                  <div v-if="hasItemError(index, 'fee_end_date')" class="fee-field-error">{{ itemError(index, 'fee_end_date') }}</div>
                </div>
              </td>
              <td>
                <div class="fee-cell-control">
                  <a-input-number v-model="item.fee_amount" class="ts-w-full" :class="{ 'is-invalid': hasItemError(index, 'fee_amount') }" disabled :min="0" :formatter="currencyFormatter" :parser="currencyParser" @change="$emit('recalculate-item', index)" />
                  <div v-if="hasItemError(index, 'fee_amount')" class="fee-field-error">{{ itemError(index, 'fee_amount') }}</div>
                </div>
              </td>
              <td>
                <a-select v-model="item.discount_type" class="ts-w-full" @change="$emit('recalculate-item', index)">
                  <a-select-option value="%">%</a-select-option>
                  <a-select-option value="$">$</a-select-option>
                </a-select>
              </td>
              <td>
                <div class="fee-cell-control">
                  <a-input-number :value="discountInputValue(item)" class="ts-w-full" :class="{ 'is-invalid': hasItemError(index, 'discount_amount') }" :min="0" :formatter="item.discount_type === '%' ? percentFormatter : currencyFormatter" :parser="amountParser" @change="onDiscountInputChange(item, index, $event)" />
                  <div v-if="hasItemError(index, 'discount_amount')" class="fee-field-error">{{ itemError(index, 'discount_amount') }}</div>
                </div>
              </td>
              <td>
                <div class="fee-cell-control">
                  <a-input-number v-model="item.amount_include_vat" class="ts-w-full" :class="{ 'is-invalid': hasItemError(index, 'amount_include_vat') }" disabled :min="0" :formatter="currencyFormatter" :parser="currencyParser" @change="$emit('sync-total', index)" />
                  <div v-if="hasItemError(index, 'amount_include_vat')" class="fee-field-error">{{ itemError(index, 'amount_include_vat') }}</div>
                </div>
              </td>
              <td class="ts-text-center">
                <a-tooltip :title="$t('preInvoiceV2.removeItem')">
                  <a-button class="fee-remove-btn" shape="circle" icon="delete" size="small" @click="$emit('remove-item', index)" />
                </a-tooltip>
              </td>
            </tr>
            <tr class="fee-add-row">
              <td colspan="11">
                <a-button type="dashed" icon="plus" block :disabled="!canLoadFees" @click="$emit('add-item')">
                  {{ $t('preInvoiceV2.addItem') }}
                </a-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import moment from 'moment'

export default {
  name: 'FeeItemsSection',
  props: {
    model: { type: Object, required: true },
    canLoadFees: { type: Boolean, required: true },
    formattedTotal: { type: String, required: true },
    feeTypes: { type: Array, required: true },
    errors: { type: Object, default: null },
    termStartDate: { type: String, default: null }
  },
  methods: {
    errorBag() {
      return this.errors && this.errors.errors ? this.errors.errors : {}
    },
    errorMessage(field) {
      return this.errors && this.errors.first ? this.errors.first(field) : ''
    },
    hasError(field) {
      return Boolean(this.errors && this.errors.has && this.errors.has(field))
    },
    feeErrorKeys() {
      return Object.keys(this.errorBag()).filter(key => key === 'items' || key.indexOf('items.') === 0)
    },
    hasFeeSectionError() {
      return this.feeErrorKeys().length > 0
    },
    feeSectionErrorMessage() {
      const keys = this.feeErrorKeys()
      if (!keys.length) return ''

      const firstKey = keys[0]
      const message = this.errorMessage(firstKey)
      return message || (Array.isArray(this.errorBag()[firstKey]) ? this.errorBag()[firstKey][0] : this.errorBag()[firstKey])
    },
    itemError(index, field) {
      return this.errorMessage(`items.${index}.${field}`)
    },
    hasItemError(index, field) {
      return this.hasError(`items.${index}.${field}`)
    },
    hasPaymentTerm(item) {
      return Boolean(item.payment_term_id && Number(item.duration_in_month || 0) > 0)
    },
    disabledStartDate(date, item) {
      const minStartDate = item.min_fee_start_date || this.termStartDate
      if (!this.hasPaymentTerm(item) || !minStartDate) return false
      return date && date.isBefore(moment(minStartDate, 'DD-MM-YYYY').startOf('day'), 'day')
    },
    isFeeSetupSelected(feeSetupId, currentIndex) {
      return this.model.items.some((item, index) => {
        return index !== currentIndex && String(item.fee_setup_id || '') === String(feeSetupId || '')
      })
    },
    currencyFormatter(value) {
      if (value === null || value === undefined || value === '') return ''
      return `$${value}`
    },
    currencyParser(value) {
      return value ? value.replace(/\$|,/g, '') : ''
    },
    percentFormatter(value) {
      if (value === null || value === undefined || value === '') return ''
      return `${value}%`
    },
    amountParser(value) {
      return value ? value.replace(/\$|%|,/g, '') : ''
    },
    discountInputValue(item) {
      return item.discount_type === '%' ? item.discount_rate : item.discount_amount
    },
    onDiscountInputChange(item, index, value) {
      if (item.discount_type === '%') {
        item.discount_rate = Number(value || 0)
      } else {
        item.discount_amount = Number(value || 0)
      }
      this.$emit('recalculate-item', index)
    }
  }
}
</script>

<style scoped>

.fee-cell-control {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.fee-section-error,
.fee-field-error {
  color: #dc2626;
  font-size: 11px;
  line-height: 1.25;
  white-space: normal;
}

.fee-section-error {
  margin: -4px 0 10px;
  padding: 7px 9px;
  border: 1px solid #fecaca;
  border-radius: 6px;
  background: #fef2f2;
  font-weight: 600;
}

.is-invalid::v-deep .ant-select-selection,
.is-invalid::v-deep .ant-input-number,
.is-invalid::v-deep .ant-calendar-picker-input {
  border-color: #dc2626 !important;
}

.section-subtitle {
  margin-top: 2px;
  margin-left: 32px;
  font-size: 11px;
  color: #6b7280;
}

.fee-toolbar {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.fee-toolbar-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
  color: #1f2937;
}

.fee-toolbar-main span {
  font-size: 12px;
  font-weight: 700;
}

.fee-toolbar-main strong {
  color: #2563eb;
  font-size: 15px;
  font-weight: 800;
}

.fee-toolbar-note {
  color: #6b7280;
  font-size: 11px;
  text-align: right;
}

.fee-empty-state {
  min-height: 104px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
}

.fee-empty-state > div {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.fee-empty-state strong {
  color: #111827;
  font-size: 13px;
  font-weight: 700;
}

.fee-empty-state span {
  color: #6b7280;
  font-size: 12px;
}

.fee-empty-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #eff6ff;
  color: #2563eb !important;
  flex: 0 0 auto;
}

.pre-invoice-table {
  min-width: 1306px;
}

.pre-invoice-table th,
.pre-invoice-table td {
  border: 1px solid #e5e7eb;
  padding: 6px;
  vertical-align: top;
}

.pre-invoice-table th {
  background: #f9fafb;
  color: #374151;
  font-size: 12px;
  font-weight: 600;
}

.fee-row-number {
  color: #6b7280;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  vertical-align: middle !important;
}

.fee-add-row td {
  padding: 10px 8px !important;
  background: #f8fafc;
}

.fee-remove-btn {
  color: #dc2626;
  border-color: #fecaca;
}

.fee-remove-btn:hover,
.fee-remove-btn:focus {
  color: #b91c1c;
  border-color: #fca5a5;
}
</style>
